import React, { useState, useEffect } from "react";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../firebase/firebase";
import { toast } from "react-toastify";
import classes from "../Signup/Signup.module.css";
import { Urls } from "../../Urls";

/**
 * VerifyEmailScreen
 *
 * Full-page card shown when a user needs to verify their email.
 * Used in two places:
 *   1. Signup.js  — right after account creation
 *   2. Login.js   — when an existing user logs in with an unverified email
 *
 * Props:
 *   email         {string}   — the address the verification was sent to
 *   onVerified    {function} — called when user confirms their email is verified
 *   onBack        {function} — called when user wants to go back (login page)
 */
const RESEND_COOLDOWN_SECONDS = 30;

const VerifyEmailScreen = ({ email, onVerified, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendLoading, setResendLoading] = useState(false);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // const handleCheckVerified = async () => {
  //   setLoading(true);
  //   try {
  //     const user = auth.currentUser;
  //     if (!user) {
  //       toast.error("Session expired. Please sign in again.");
  //       onBack();
  //       return;
  //     }
  //     // Force-refresh Firebase's cached user object so emailVerified is current
  //     await user.reload();
  //     const refreshed = auth.currentUser;
  //     if (!refreshed.emailVerified) {
  //       toast.info("Email not verified yet. Please click the link in your inbox first.");
  //       setLoading(false);
  //       return;
  //     }
  //     // Verified — hand control back to the caller
  //     onVerified();
  //   } catch (err) {
  //     console.error("[VerifyEmailScreen] checkVerified error:", err);
  //     toast.error("Could not check verification status. Please try again.");
  //     setLoading(false);
  //   }
  // };

//   const handleCheckVerified = async () => {
//   setLoading(true);
//   try {
//     const user = auth.currentUser;
//     if (!user) {
//       toast.error("Session expired. Please sign in again.");
//       onBack();
//       return;
//     }
//     await user.reload();
//     const refreshed = auth.currentUser;
//     if (!refreshed.emailVerified) {
//       toast.info("Email not verified yet. Please click the link in your inbox first.");
//       setLoading(false);
//       return;
//     }
//     // Verified — hand control back to the caller
//     // setLoading(false);  // ← add this line
//     setLoading(false)
//     onVerified();
//   } catch (err) {
//     console.error("[VerifyEmailScreen] checkVerified error:", err);
//     toast.error("Could not check verification status. Please try again.");
//     setLoading(false);
//   }
// };
const handleCheckVerified = async () => {
  setLoading(true);
  try {
    const user = auth.currentUser;
    if (!user) {
      toast.error("Session expired. Please sign in again.");
      onBack();
      return;
    }
    // Force-refresh Firebase's cached user object so emailVerified is current
    await user.reload();
    const refreshed = auth.currentUser;
    if (!refreshed.emailVerified) {
      toast.info("Email not verified yet. Please click the link in your inbox first.");
      setLoading(false);
      return;
    }
    // Force Firebase to re-emit auth state so onAuthStateChanged
    // fires again in App.js and picks up MFA_VERIFIED_KEY
    await refreshed.getIdToken(true);
    setLoading(false);
    onVerified();
  } catch (err) {
    console.error("[VerifyEmailScreen] checkVerified error:", err);
    toast.error("Could not check verification status. Please try again.");
    setLoading(false);
  }
};
  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Session expired. Please sign in again.");
        onBack();
        return;
      }
      await sendEmailVerification(user);
      toast.success("Verification email resent! Please check your inbox.");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      console.error("[VerifyEmailScreen] resend error:", err);
      if (err.code === "auth/too-many-requests") {
        toast.error("Too many requests. Please wait a few minutes before resending.");
      } else {
        toast.error("Could not resend verification email. Please try again.");
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={classes["right-content-wrapper"]}>
      <div className={classes["right-logo-container"]}>
        <img
          alt="LoRRI logo"
          className={classes["right-logo"]}
          src={Urls.LorriLogoLogin}
        />
      </div>

      <div className={classes["signup-card"]}>
        {/* Icon */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "var(--purple-100)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 18px",
            color: "var(--purple-700)",
            fontSize: 24,
          }}
        >
          <i className="far fa-envelope" />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: "var(--ink-900)",
            textAlign: "center",
            marginBottom: 10,
            letterSpacing: "-0.02em",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          Verify your email
        </h2>

        {/* Description */}
        <p
          style={{
            fontSize: 13.5,
            color: "var(--ink-600)",
            textAlign: "center",
            lineHeight: 1.6,
            marginBottom: 20,
          }}
        >
          We've sent a verification link to{" "}
          <strong style={{ color: "var(--purple-700)" }}>{email}</strong>.
          <br />
          Click the link in your inbox, then press the button below.
        </p>

        {/* Info box */}
        <div
          style={{
            background: "#f7faff",
            border: "1px solid #dbeafe",
            borderLeft: "4px solid var(--purple-700)",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 12,
            color: "#1e40af",
            marginBottom: 20,
            lineHeight: 1.5,
          }}
        >
          <i className="fas fa-info-circle" style={{ marginRight: 7 }} />
          Didn't get the email? Check your spam folder or use the resend button below.
        </div>

        {/* Primary CTA */}
        <button
          className={classes["submit-btn"]}
          onClick={handleCheckVerified}
          disabled={loading}
          style={{ opacity: loading ? 0.7 : 1 }}
        >
          {loading ? "Checking…" : "I've verified my email →"}
        </button>

        {/* Resend */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || resendLoading}
            style={{
              background: "none",
              border: "none",
              color:
                resendCooldown > 0 || resendLoading
                  ? "var(--ink-400)"
                  : "var(--purple-700)",
              fontWeight: 700,
              fontSize: 13,
              cursor:
                resendCooldown > 0 || resendLoading ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              padding: 0,
            }}
          >
            {resendLoading
              ? "Sending…"
              : resendCooldown > 0
              ? `Resend email (${resendCooldown}s)`
              : "Resend verification email"}
          </button>
        </div>

        {/* Back link */}
        <div
          className={classes["login-link-container"]}
          style={{ borderTop: "1px solid var(--ink-100)", paddingTop: 14 }}
        >
          <span
            className={classes["login-link"]}
            onClick={onBack}
          >
            <i className="fa fa-arrow-left" style={{ marginRight: 6 }} />
            Back to Sign In
          </span>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailScreen;